// The owner's subscription warning (UC-24), in the owner's language.
//
// The server decides the notice and the days left, on the restaurant's own
// calendar; this only words it. The end date is a calendar day, so it is read
// as that day and never moved by this browser's time zone.

import { fill, isArabic, message, type Language } from '../i18n/messages';

export interface SubscriptionNotice {
	endsOn: string;
	daysLeft: number;
	notice: 'none' | 'ending' | 'ended';
}

/** "12 days", and Arabic's own counting: يوم واحد، يومين، 3 أيام، 12 يوم. */
export function daysPhrase(language: Language, n: number): string {
	if (n === 1) return message(language, 'subscriptionOneDay');
	if (n === 2) return message(language, 'subscriptionTwoDays');
	return fill(message(language, n <= 10 ? 'subscriptionFewDays' : 'subscriptionManyDays'), n);
}

/** The end date as "31 Dec" or "31 ديسمبر". */
export function endDay(language: Language, endsOn: string): string {
	const at = new Date(`${endsOn}T00:00:00Z`);
	if (Number.isNaN(at.getTime())) return endsOn;
	const parts = new Intl.DateTimeFormat(isArabic(language) ? 'ar-EG-u-nu-latn' : 'en-US', {
		day: 'numeric',
		month: isArabic(language) ? 'long' : 'short',
		timeZone: 'UTC',
	}).formatToParts(at);
	const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
	return `${part('day')} ${part('month')}`;
}

/** The warning's one line, or empty when there is nothing to warn about. */
export function subscriptionLine(language: Language, sub: SubscriptionNotice): string {
	if (sub.notice === 'ended')
		return fill(message(language, 'subscriptionEnded'), endDay(language, sub.endsOn));
	if (sub.notice !== 'ending') return '';
	if (sub.daysLeft === 0) return message(language, 'subscriptionEndsToday');
	return fill(
		message(language, 'subscriptionEndsOn'),
		endDay(language, sub.endsOn),
		daysPhrase(language, sub.daysLeft),
	);
}
